import { getDefaultSortKey } from '@components/verktoy-liste/verktoy-filter/helpers/filter-helpers';
import { useGetResources } from '@hooks/useGetResources';
import useQueryParam from '@hooks/useQueryParams';
import useQueryParamMulti from '@hooks/useQueryParamsMultiValue';
import { URLQueryParamNames } from '@src/types/urlQueryParamNames';
import { useAppDispatch, useAppSelector } from '@store';
import { getDefaultVerktoyFilters } from '@utils/filter-utils';
import { hentSokParam, getFagomraadeTyperQueryMaps, getMalgrupperQueryMaps, getVerktoyTyperQueryMaps } from '@utils/queryParamUtil';

import type { VerktoyFiltersSettings } from '@components/verktoy-liste/verktoy-filter/types/verktoy-filter-settings';

import { useFilters } from '@helsenorge/filter/components/filter/useFilters';

/** En "wrapper" Helper Hook for å å lettere kordinere bruken av QueryParams hookene */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useVerktoyFilterState() {
  const resources = useGetResources();
  const dispatch = useAppDispatch();
  const paramsLowerCase = hentSokParam(resources);
  const [stotterAnonymOppstartChecked, setStotterAnonymOppstartChecked] = useQueryParam(
    URLQueryParamNames.StotterAnonymOppstart,
    paramsLowerCase
  );
  const fagomraader = getFagomraadeTyperQueryMaps(resources);
  const malgrupper = getMalgrupperQueryMaps(resources);
  const verktoytyper = getVerktoyTyperQueryMaps(resources);
  const [fagomraderValgt, setFagomraderValgt] = useQueryParamMulti(URLQueryParamNames.Fagomrade, paramsLowerCase, fagomraader);
  const [verktoytyperValgt, setVerktoyTyperValgt] = useQueryParamMulti(URLQueryParamNames.VerktoyType, paramsLowerCase, verktoytyper);
  const [malgrupperValgt, setMalgrupperValgt] = useQueryParamMulti(URLQueryParamNames.Malgruppe, paramsLowerCase, malgrupper);
  const verktoyFiltersSettings: VerktoyFiltersSettings = {
    stotterAnonymOppstartChecked,
    stotterAnonymOppstartCheckedValue: resources?.visKunVerktoySomKanApnesAnonymt,
    fagomraadeValueFromUrl: fagomraader,
    fagomraadeValgt: fagomraderValgt,
    malgrupperValueFromUrl: malgrupper,
    malgrupperValgt,
    verktoytyperValueFromUrl: verktoytyper,
    verktoytyperValgt,
  };
  const defaultValues = getDefaultVerktoyFilters(verktoyFiltersSettings);
  const [searchValueUrl, setSearchValueUrl] = useQueryParam(URLQueryParamNames.Sok, paramsLowerCase);
  const [valgtSortering, setValgtSortering] = useQueryParam(URLQueryParamNames.Sortering, paramsLowerCase);
  const [valgtSorterstigende, setValgtSorterstigende] = useQueryParam(URLQueryParamNames.SorterStigende, paramsLowerCase);
  const isFilterOpen = useAppSelector(globalState => globalState.verktoy.isFilterOpen);
  const { filters, setFilter, updateFilters, activeFilters, setDelayedFilter, searchQuery, setSearchQuery, sortKey, setSortKey } =
    useFilters({
      defaultValues,
      sortKey: getDefaultSortKey(valgtSortering, valgtSorterstigende),
    });

  return {
    resources,
    dispatch,
    paramsLowerCase,
    stotterAnonymOppstartChecked,
    setStotterAnonymOppstartChecked,
    fagomraader,
    fagomraderValgt,
    setFagomraderValgt,
    malgrupper,
    malgrupperValgt,
    setMalgrupperValgt,
    verktoytyper,
    verktoytyperValgt,
    setVerktoyTyperValgt,
    verktoyFiltersSettings,
    defaultValues,
    searchValueUrl,
    setSearchValueUrl,
    valgtSortering,
    setValgtSortering,
    valgtSorterstigende,
    setValgtSorterstigende,
    isFilterOpen,
    filters,
    setFilter,
    updateFilters,
    activeFilters,
    setDelayedFilter,
    searchQuery,
    setSearchQuery,
    sortKey,
    setSortKey,
  };
}

export default useVerktoyFilterState;
